<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>
                Themes
            </template>
            <template v-slot:pageDetail>
                Global Theme Customizer
            </template>
        </o-bread-crumb>

        <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>

        <!-- Toast Notification -->
        <ToastNotification
            v-model="showToast"
            :type="toastType"
            :title="toastTitle"
            :message="toastMessage"
            :duration="2000"
            position="top-right"
        />

        <!-- Tab Navigation -->
        <div class="theme-tabs">
            <button
                class="theme-tab"
                :class="{ 'theme-tab--active': activeTab === 'themes' }"
                @click="activeTab = 'themes'"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 2a4.5 4.5 0 0 0 0 9 4.5 4.5 0 0 1 0 9 10 10 0 0 0 0-20z"/>
                </svg>
                Themes
            </button>
            <button
                class="theme-tab"
                :class="{ 'theme-tab--active': activeTab === 'others' }"
                @click="activeTab = 'others'"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M3 9h18M9 3v18"/>
                </svg>
                Media & Branding
            </button>
        </div>

        <!-- THEMES TAB -->
        <div v-if="activeTab === 'themes'" class="theme-layout">
            <!-- Left Panel: Controls -->
            <div class="theme-panel theme-panel--controls">
                <!-- Preset Picker -->
                <o-theme-preset-picker
                    :active-preset-id="matchedPresetId"
                    @select="applyPreset"
                />

                <!-- Divider -->
                <div class="theme-divider">
                    <span>or customize manually</span>
                </div>

                <!-- Custom Color Pickers -->
                <div class="color-section">
                    <h3 class="color-section__title"><i class="fas fa-palette text-primary me-2"></i> Custom Colors</h3>

                    <div class="color-field" v-for="field in colorFields" :key="field.key">
                        <label class="color-field__label">{{ field.label }}</label>
                        <div class="color-field__row">
                            <div class="color-field__preview" :style="{ backgroundColor: tempColors[field.key] }"></div>
                            <input
                                type="text"
                                class="color-field__input"
                                v-model="tempColors[field.key]"
                                :placeholder="field.placeholder"
                            />
                            <input
                                type="color"
                                class="color-field__picker"
                                v-model="tempColors[field.key]"
                            />
                        </div>
                        <div v-if="validation[field.key]" class="color-field__error">
                            {{ validation[field.key][0] }}
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="theme-actions">
                    <button type="button" class="btn-theme btn-theme--save" @click="handleSubmit" :disabled="page.isLoading">
                        <span v-if="page.isLoading">Saving...</span>
                        <span v-else>Save Changes</span>
                    </button>
                    <button type="button" class="btn-theme btn-theme--secondary" @click="resetColors">
                        Reset to Saved
                    </button>
                </div>
            </div>

            <!-- Right Panel: Live Preview -->
            <div class="theme-panel theme-panel--preview">
                <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
                    <h3 class="preview-title" style="margin-bottom: 0;"><i class="fas fa-desktop text-primary me-2"></i> Live Preview</h3>
                    <div class="preview-tab-pill">
                        <button 
                            type="button"
                            class="pill-btn" 
                            :class="{ 'active': activePreviewTab === 'admin' }" 
                            @click="activePreviewTab = 'admin'"
                        >
                            Admin UI
                        </button>
                        <button 
                            type="button"
                            class="pill-btn" 
                            :class="{ 'active': activePreviewTab === 'member' }" 
                            @click="activePreviewTab = 'member'"
                        >
                            Member UI
                        </button>
                    </div>
                </div>
                
                <!-- Desktop Admin Preview Component -->
                <o-theme-admin-desktop-preview 
                    v-if="activePreviewTab === 'admin'" 
                    :colors="tempColors" 
                />
                
                <!-- Mobile Member Preview Component -->
                <o-theme-member-mobile-preview 
                    v-else 
                    :colors="tempColors" 
                />
            </div>
        </div>

        <!-- OTHERS TAB -->
        <div v-if="activeTab === 'others'" class="theme-layout theme-layout--others">
            <div class="theme-panel theme-panel--controls">
                <div class="media-branding-header">
                    <h3 class="color-section__title mb-1"><i class="fas fa-images text-primary me-2"></i> Media & Branding</h3>
                    <p class="color-section__subtitle">Customize the appearance of your application with specific branded assets.</p>
                </div>

                <!-- Section: SEO & Meta -->
                <div class="media-card">
                    <div class="media-card__header">
                        <h4><i class="fas fa-globe me-2"></i> Tab Title</h4>
                        <p>The text displayed in the browser tab and search engines.</p>
                    </div>
                    <div class="media-card__body">
                        <div class="o-input-wrap">
                            <input
                                type="text"
                                class="o-input"
                                v-model="othersData.pageTitle"
                                placeholder="e.g. Membership App"
                                @input="handlePageTitleInput"
                            />
                        </div>
                    </div>
                </div>

                <!-- Section: Primary Assets -->
                <div class="media-card">
                    <div class="media-card__header">
                        <h4><i class="fas fa-image me-2"></i> Primary Assets</h4>
                        <p>Main visuals used on the login page and application headers.</p>
                    </div>
                    <div class="media-card__body">
                        <div class="media-grid">
                            <div class="media-item">
                                <label>Login Background <span class="badge-res" title="Best resolution: 1440 x 1024 px. Max size 5MB.">1440 × 1024 <i class="fas fa-info-circle" style="font-size: 9px; opacity: 0.7; margin-left: 2px;"></i></span></label>
                                <o-image-input name="login_background" id="login_background"
                                    :imageurl="othersData.loginBackground"
                                    :skipCompression="true"
                                    @selectImage="(file) => handleImageUpload(file, 1440, 1024, 'loginBackground')"
                                />
                            </div>
                            <div class="media-item">
                                <label>Corporate Logo <span class="badge-res" title="Best resolution: 240 x 240 px (Square 1:1 format).">240 × 240 <i class="fas fa-info-circle" style="font-size: 9px; opacity: 0.7; margin-left: 2px;"></i></span></label>
                                <o-image-input name="logo" id="logo"
                                    :imageurl="othersData.corporateLogo"
                                    :skipCompression="true"
                                    @selectImage="(file) => handleImageUpload(file, 240, 240, 'corporateLogo')"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section: Favicons -->
                <div class="media-card">
                    <div class="media-card__header">
                        <h4><i class="fas fa-icons me-2"></i> Browser Icons</h4>
                        <p>Small icons displayed in the browser tab.</p>
                    </div>
                    <div class="media-card__body">
                        <div class="media-grid">
                            <div class="media-item">
                                <label>Favicon <span class="badge-res" title="Recommended size: 32 x 32 px (.png or .ico)">32 × 32 <i class="fas fa-info-circle" style="font-size: 9px; opacity: 0.7; margin-left: 2px;"></i></span></label>
                                <o-image-input name="favicon32" id="favicon32"
                                    :imageurl="othersData.favicon32"
                                    :skipCompression="true"
                                    @selectImage="(file) => handleImageUpload(file, 32, 32, 'favicon32')"
                                />
                            </div>
                            <div class="media-item">
                                <label>Favicon <span class="badge-res" title="Recommended size: 16 x 16 px (.png or .ico)">16 × 16 <i class="fas fa-info-circle" style="font-size: 9px; opacity: 0.7; margin-left: 2px;"></i></span></label>
                                <o-image-input name="favicon16" id="favicon16"
                                    :imageurl="othersData.favicon16"
                                    :skipCompression="true"
                                    @selectImage="(file) => handleImageUpload(file, 16, 16, 'favicon16')"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="theme-actions mt-4">
                    <button type="button" class="btn-theme btn-theme--save" @click="handleSubmit" :disabled="page.isLoading">
                        <span v-if="page.isLoading">Saving...</span>
                        <span v-else>Save Changes</span>
                    </button>
                    <button type="button" class="btn-theme btn-theme--secondary" @click="resetMedia">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="page.isLoading" class="d-flex justify-content-center spinner-border text-primary spinner-position form"></div>
    <div v-if="page.isLoading" class="spinner"></div>
</template>

<script>
import {OBreadCrumb, OImageInput, OThemePresetPicker, OThemeAdminDesktopPreview, OThemeMemberMobilePreview} from '@/components/OmnihControl';
import ToastNotification from '@/components/member/ToastNotification.vue';
import { reactive, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import store from '@/store';
import { themePresets } from '@/config/themePresets';

export default {
    name: 'AddThemeCorporate',
    components: {
        OBreadCrumb,
        OImageInput,
        OThemePresetPicker,
        OThemeAdminDesktopPreview,
        OThemeMemberMobilePreview,
        ToastNotification
    },
    setup() {
        const page = reactive({
            isLoading: false,
            topCardTitle: 'Sub Settings',
            leftCardTitle: 'Themes Corporate',
            rightCardTitle: 'Others',
        });

        const activeTab = ref('themes');
        const activePreviewTab = ref('admin');

        const themeCorporate = reactive({
            id: null,
            corporate_id: '',
            top_navbar: '',
            sidebar_navbar: '',
            bottom_navbar: '',
            text_navbar: '',
            primary_button: '',
            text_button: '',
            login_background: '',
            logo: '',
            favicon_32: '',
            favicon_16: '',
            page_title: '',
        });

        const othersData = reactive({
            pageTitle: localStorage.getItem('page_title') ?? '',
            corporateLogo: localStorage.getItem('corporateLogo') ?? '',
            loginBackground: localStorage.getItem('login_background') || localStorage.getItem('loginBackground') || '',
            favicon32: localStorage.getItem('favicon32') ?? '',
            favicon16: localStorage.getItem('favicon16') ?? '',
        });

        const tempColors = reactive({
            top_navbar: '',
            sidebar_navbar: '',
            bottom_navbar: '',
            text_navbar: '',
            primary_button: '',
            text_button: ''
        });

        /** Color field definitions for rendering */
        const colorFields = [
            { key: 'top_navbar', label: 'Top Navbar', placeholder: '#FC6666' },
            { key: 'sidebar_navbar', label: 'Sidebar Navbar', placeholder: '#CB5B5B' },
            { key: 'bottom_navbar', label: 'Bottom Navbar', placeholder: '#663434' },
            { key: 'text_navbar', label: 'Text Navbar', placeholder: '#FFFFFF' },
            { key: 'primary_button', label: 'Primary / Brand Color', placeholder: '#F93E3E' },
            { key: 'text_button', label: 'Button Text', placeholder: '#FFFFFF' },
        ];

        /**
         * Match current tempColors against presets to detect which preset is active
         */
        const matchedPresetId = computed(() => {
            const match = themePresets.find((p) => {
                return Object.keys(p.colors).every(
                    (key) => p.colors[key]?.toLowerCase() === tempColors[key]?.toLowerCase()
                );
            });
            return match ? match.id : null;
        });

        const validation = ref([]);
        const error = ref('');
        const router = useRouter();

        // Toast state
        const showToast = ref(false);
        const toastType = ref('success');
        const toastTitle = ref('');
        const toastMessage = ref('');

        const selectedBusinessProfile = reactive({
            corporate_id: '',
            branch_id: '',
            merchant_id: ''
        });

        onMounted(() => {
            page.isLoading = true;
            loadBusinessProfile();
            Object.keys(tempColors).forEach(key => {
                tempColors[key] = themeCorporate[key];
            });
            page.isLoading = false;
            window.addEventListener('storage', handleStorageChange);
        });

        onUnmounted(() => {
            window.removeEventListener('storage', handleStorageChange);
        });

        async function loadState(id) {
            page.isLoading = true;

            try {
                const data = await store.dispatch('ThemeCorporate/read_single', id);
                
                if (Array.isArray(data) && data.length === 0) {
                    return;
                }

                const themeData = Array.isArray(data) ? data[0] : data;
                
                if (themeData && themeData.id) {
                    themeCorporate.id = themeData.id;
                    const fields = [
                        'top_navbar',
                        'sidebar_navbar',
                        'bottom_navbar',
                        'text_navbar',
                        'primary_button',
                        'text_button'
                    ];

                    fields.forEach(field => {
                        const colorValue = themeData[field];
                        themeCorporate[field] = colorValue;
                        tempColors[field] = colorValue;
                    });

                    themeCorporate.corporate_id = themeData.corporate_id;
                }
            } catch (error) {
                console.error('Error loading theme state:', error);
            } finally {
                page.isLoading = false;
            }
        }

        function loadBusinessProfile() {
            store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                selectedBusinessProfile.branch_id = businessProfile.branch_id;
                selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                
                loadState(selectedBusinessProfile.corporate_id);
            });
        }

        /**
         * Apply a preset's colors to the temp color form
         */
        function applyPreset(preset) {
            Object.keys(preset.colors).forEach((key) => {
                tempColors[key] = preset.colors[key];
            });
        }

        /**
         * Reset temp colors back to last saved values
         */
        function resetColors() {
            Object.keys(tempColors).forEach(key => {
                tempColors[key] = themeCorporate[key];
            });
        }

        function handleSubmit() {
            Object.keys(tempColors).forEach(key => {
                themeCorporate[key] = tempColors[key];
            });

            themeCorporate.corporate_id = selectedBusinessProfile.corporate_id;

            change();
        }

        function change() {
            const data = { ...themeCorporate };
            page.isLoading = true;

            store.dispatch('ThemeCorporate/update', { data, id: data.corporate_id })
            .then(() => {
                return store.dispatch('ThemeCorporate/read_single', data.corporate_id);
            })
            .then(() => {
                // success toast
                toastType.value = 'success';
                toastTitle.value = 'Success';
                toastMessage.value = 'Theme updated successfully!';
                showToast.value = true;
                // Auto reload
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            }).catch((err) => {
                if (err.response && err.response.status === 403) {
                    error.value = err.response.data.message;
                } else {
                    validation.value = JSON.parse(err.response.data.error);
                }
                // error toast
                toastType.value = 'error';
                toastTitle.value = 'Error';
                toastMessage.value = 'Failed to update theme. Please try again.';
                showToast.value = true;
            }).finally(() => {
                themeCorporate.login_background = '',
                themeCorporate.logo = '',
                themeCorporate.favicon_16 = '',
                themeCorporate.favicon_32 = '',
                themeCorporate.page_title = ''
                page.isLoading = false;
            });
        }

        function cancel() {
            router.push('/dashboard');
        }

        function resetMedia() {
            othersData.pageTitle = localStorage.getItem('page_title') ?? '';
            othersData.corporateLogo = localStorage.getItem('corporateLogo') ?? '';
            othersData.loginBackground = localStorage.getItem('login_background') || localStorage.getItem('loginBackground') || '';
            othersData.favicon32 = localStorage.getItem('favicon32') ?? '';
            othersData.favicon16 = localStorage.getItem('favicon16') ?? '';

            themeCorporate.page_title = '';
            themeCorporate.logo = '';
            themeCorporate.login_background = '';
            themeCorporate.favicon_32 = '';
            themeCorporate.favicon_16 = '';
        }

        function resizeImage(file, width, height) {
            const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new Image();
                    img.src = e.target.result;
                    
                    img.onload = () => {
                        const originalRatio = img.width / img.height;
                        const targetRatio = width / height;

                        let drawWidth, drawHeight, offsetX = 0, offsetY = 0;

                        if (originalRatio > targetRatio) {
                            drawWidth = width;
                            drawHeight = width / originalRatio;
                            offsetY = (height - drawHeight) / 2;
                        } else {
                            drawHeight = height;
                            drawWidth = height * originalRatio;
                            offsetX = (width - drawWidth) / 2;
                        }

                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        
                        const ctx = canvas.getContext('2d');
                        ctx.clearRect(0, 0, width, height);
                        
                        ctx.drawImage(
                            img, 
                            offsetX, 
                            offsetY, 
                            drawWidth, 
                            drawHeight
                        );

                        canvas.toBlob(
                            (blob) => {
                                resolve(new File([blob], fileName, {
                                    type: 'image/png',
                                    lastModified: Date.now()
                                }));
                            },
                            'image/png',
                            1
                        );
                    };
                };
                const convertedFile = base64ToFile(file, fileName);
                
                reader.readAsDataURL(convertedFile);
            });
        }

        function resizeImageBase64(base64String, targetWidth, targetHeight) {
            return new Promise((resolve) => {
                const img = new Image();
                img.src = base64String;
                
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = targetWidth;
                    canvas.height = targetHeight;
                    
                    const ctx = canvas.getContext('2d');
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';

                    const srcRatio = img.width / img.height;
                    const targetRatio = targetWidth / targetHeight;

                    let srcX = 0, srcY = 0, srcWidth = img.width, srcHeight = img.height;

                    if (srcRatio > targetRatio) {
                        srcWidth = img.height * targetRatio;
                        srcX = (img.width - srcWidth) / 2;
                    } else {
                        srcHeight = img.width / targetRatio;
                        srcY = (img.height - srcHeight) / 2;
                    }

                    ctx.drawImage(
                        img,
                        srcX,
                        srcY,
                        srcWidth,
                        srcHeight,
                        0,
                        0,
                        targetWidth,
                        targetHeight
                    );

                    const base64 = canvas.toDataURL('image/png');
                    resolve(base64);
                };
            });
        }

        function base64ToFile(base64, fileName) {
            const arr = base64.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            
            while(n--){
                u8arr[n] = bstr.charCodeAt(n);
            }
            
            return new File([u8arr], fileName, {type: mime});
        }

        async function handleImageUpload(base64String, width, height, imageType) {
            try {

                const resizedFile = await resizeImage(base64String, width, height);
                const url = URL.createObjectURL(resizedFile);
                const resizedBase64 = await resizeImageBase64(base64String, width, height);
                

                switch(imageType) {
                    case 'loginBackground':
                        themeCorporate.login_background = resizedBase64;
                        othersData.loginBackground = url;
                        break;
                    case 'corporateLogo': {
                        const file = base64ToFile(base64String, 'corporateLogo');
                        const urlNotResizedBase64 = URL.createObjectURL(file);
                        themeCorporate.logo = base64String;
                        othersData.corporateLogo = urlNotResizedBase64;
                        break;
                    }
                    case 'favicon32':
                        themeCorporate.favicon_32 = resizedBase64;
                        othersData.favicon32 = url;
                        break;
                    case 'favicon16':
                        themeCorporate.favicon_16 = resizedBase64;
                        othersData.favicon16 = url;
                        break;
                }
            } catch (error) {
                console.error('Error processing image:', error);
            }
        }

        const handleStorageChange = (event) => {
            if (event.key === 'page_title') othersData.pageTitle = event.newValue;
            if (event.key === 'corporateLogo') othersData.corporateLogo = event.newValue;
            if (event.key === 'login_background' || event.key === 'loginBackground') othersData.loginBackground = event.newValue;
            if (event.key === 'favicon32') othersData.favicon32 = event.newValue;
            if (event.key === 'favicon16') othersData.favicon16 = event.newValue;
        };

        function handlePageTitleInput(event) {
            if (event.target.value !== localStorage.getItem('page_title')) {
                themeCorporate.page_title = event.target.value;
            } else {
                themeCorporate.page_title = '';
            }
        }

        return {
            activeTab,
            activePreviewTab,
            themeCorporate,
            tempColors,
            colorFields,
            matchedPresetId,
            validation,
            router,
            handleSubmit,
            page,
            selectedBusinessProfile,
            cancel,
            error,
            showToast,
            toastType,
            toastTitle,
            toastMessage,
            handleImageUpload,
            othersData,
            handlePageTitleInput,
            applyPreset,
            resetColors,
            resetMedia,
        }
    }
}
</script>

<style scoped>
/* ===== TAB NAVIGATION ===== */
.theme-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 24px;
    background: #F5F6FA;
    padding: 4px;
    border-radius: 12px;
    width: fit-content;
}

.theme-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #717784;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.theme-tab:hover {
    color: #1B1E28;
}

.theme-tab--active {
    background: #FFFFFF;
    color: #1B1E28;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    font-weight: 600;
}

/* ===== LAYOUT ===== */
.theme-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    align-items: start;
}

.theme-layout--others {
    grid-template-columns: 1fr;
    max-width: 800px;
}

.theme-panel {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.theme-panel--preview {
    position: sticky;
    top: 80px;
}

/* ===== DIVIDER ===== */
.theme-divider {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 24px 0;
}

.theme-divider::before,
.theme-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #E1E4EA;
}

.theme-divider span {
    font-size: 12px;
    color: #A0A5B0;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

/* ===== COLOR SECTION ===== */
.color-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.color-section__title {
    font-size: 15px;
    font-weight: 600;
    color: #1B1E28;
    margin: 0;
}

.color-section__subtitle {
    font-size: 13px;
    color: #717784;
    margin: 0 0 8px;
}

.color-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.color-field__label {
    font-size: 13px;
    font-weight: 500;
    color: #535862;
}

.color-field__row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.color-field__preview {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #E1E4EA;
    flex-shrink: 0;
}

.color-field__input {
    flex: 1;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #E1E4EA;
    border-radius: 8px;
    font-size: 13px;
    color: #1B1E28;
    outline: none;
    transition: border-color 0.2s;
}

.color-field__input:focus {
    border-color: var(--primary-border, #F93E3E);
    box-shadow: 0 0 0 3px rgba(249, 62, 62, 0.08);
}

.color-field__input--full {
    width: 100%;
}

.color-field__picker {
    width: 36px;
    height: 36px;
    padding: 2px;
    border: 1px solid #E1E4EA;
    border-radius: 8px;
    cursor: pointer;
    background: transparent;
}

.color-field__error {
    font-size: 12px;
    color: #dc2626;
}

/* ===== ACTIONS ===== */
.theme-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #E1E4EA;
}

.btn-theme {
    padding: 10px 24px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
}

.btn-theme--save {
    background: var(--primary-color, #F93E3E);
    color: #FFFFFF;
}

.btn-theme--save:hover {
    opacity: 0.9;
}

.btn-theme--save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-theme--secondary {
    background: #ffffff;
    color: var(--primary-color, #F93E3E);
    border: 1px solid var(--primary-border, #F93E3E);
}

.btn-theme--secondary:hover {
    background: var(--secondary-color, #F93E3E);
}

/* ===== LIVE PREVIEW ===== */
.preview-title {
    font-size: 15px;
    font-weight: 600;
    color: #1B1E28;
    margin: 0 0 16px;
}

.preview-frame {
    border: 1px solid #E1E4EA;
    border-radius: 10px;
    overflow: hidden;
    background: #F5F6FA;
}

.preview-topbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
}

.preview-topbar__logo {
    font-size: 13px;
    font-weight: 600;
}

.preview-topbar__user {
    font-size: 12px;
}

.preview-body {
    display: flex;
    min-height: 320px;
}

.preview-sidebar {
    width: 120px;
    padding: 12px 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;
}

.preview-sidebar__item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    cursor: default;
}

.preview-sidebar__item--active {
    background: rgba(255, 255, 255, 0.15);
}

.preview-sidebar__bottom {
    margin-top: auto;
    padding: 8px;
    border-radius: 6px;
    text-align: center;
}

.preview-content {
    flex: 1;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #FFFFFF;
}

.preview-hero {
    padding: 16px;
    border-radius: 8px;
    color: #FFFFFF;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.preview-hero__text {
    font-size: 11px;
    opacity: 0.9;
}

.preview-hero__sub {
    font-size: 15px;
    font-weight: 600;
}

.preview-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.preview-card {
    background: #F5F6FA;
    border-radius: 8px;
    padding: 10px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;
}

.preview-card__icon {
    font-size: 18px;
}

.preview-card__label {
    font-size: 10px;
    color: #717784;
}

.preview-card__value {
    font-size: 14px;
    font-weight: 600;
    color: #1B1E28;
}

.preview-btn-row {
    display: flex;
    gap: 8px;
}

.preview-btn {
    flex: 1;
    padding: 8px 12px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: default;
}

.preview-btn--outline {
    background: transparent;
    border: 2px solid;
}

/* ===== MEDIA & BRANDING ===== */
.media-branding-header {
    margin-bottom: 24px;
}

.media-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
}

.media-card__header {
    margin-bottom: 16px;
    border-bottom: 1px dashed #e2e8f0;
    padding-bottom: 12px;
}

.media-card__header h4 {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px 0;
}

.media-card__header p {
    font-size: 11px;
    color: #64748b;
    margin: 0;
}

.media-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
}


/* Override OImageInput default behavior locally */
.theme-layout--others :deep(.upload-wrapper.has-image) {
    height: 180px !important;
    width: 100% !important;
    border: 2px solid #e2e8f0 !important;
    min-height: auto !important;
}

.theme-layout--others :deep(.preview-img) {
    width: 100% !important;
    height: 100% !important;
    object-fit: scale-down !important;
    padding: 12px !important;
}

.media-item label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 10px;
}

.badge-res {
    background: #f1f5f9;
    color: #64748b;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    font-family: monospace;
    cursor: help;
}

.o-input-wrap {
    position: relative;
}

.o-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 13px;
    color: #1e293b;
    transition: all 0.2s;
}

.o-input:focus {
    outline: none;
    border-color: var(--primary-border, #F93E3E);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.1));
}

/* ===== RESPONSIVE ===== */
@media (max-width: 992px) {
    .theme-layout {
        grid-template-columns: 1fr;
    }

    .theme-panel--preview {
        position: static;
    }
}

@media (max-width: 576px) {
    .media-grid {
        grid-template-columns: 1fr;
    }
}

/* Preview Tab Pills */
.preview-tab-pill {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    gap: 4px;
}

.pill-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
}

.pill-btn.active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
</style>